import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import parse from 'html-react-parser';
import { ChatProps } from './props';
import UploadFileInput from './components/UploadFileInput';
import {
  AvatarExamOption,
  AvatarExamPayload,
  AvatarExamQuestion,
  AvatarResponse,
  isConversationEndedResponse,
} from '../../../models/avatar';
import LoadingMessage from './components/LoadingMessage';
import FileDescriptionInput from './components/FileDescriptionInput';
import LoadingSpinner from '../LoadingSpinner';
import UploadFileSendBtn from './components/UploadFileSendBtn';
import UploadFileCancelBtn from './components/UploadFileCancelBtn';
import FileIcon from './components/FileIcon';
import { createEventListeners } from '../../../helpers/button';
import { useAvatarContext } from '../../../context/Avatar/AvatarContext';
import { useChatContext } from '../../../context/Chat/Chat';
import { downloadElementAsPdf } from '../../../helpers/pdf';
import { showToast } from '../../../helpers/toast';
import { ReactComponent as ThumbUpIcon } from '../../../assets/images/thumb-up.svg';
import { ReactComponent as ThumbDownIcon } from '../../../assets/images/thumb-down.svg';

type ExamAnswers = Record<string, unknown>;

const getQuestionOptions = (question: AvatarExamQuestion): AvatarExamOption[] =>
  question.datos?.opciones ?? question.datos?.fragmentos ?? [];

const getHuecoIds = (question: AvatarExamQuestion): string[] => {
  const textWithGaps = question.datos?.texto_con_huecos ?? '';
  const matches = textWithGaps.match(/\[([^\]]+)\]/g) ?? [];

  return [...new Set(matches.map((match) => match.slice(1, -1)))];
};

const getOrderedItems = (
  question: AvatarExamQuestion,
  answer: unknown
): AvatarExamOption[] => {
  const defaultItems = getQuestionOptions(question);
  const selectedOrder = Array.isArray(answer)
    ? (answer as string[])
    : defaultItems.map((item) => item.id);

  return selectedOrder
    .map((itemId) => defaultItems.find((item) => item.id === itemId))
    .filter(Boolean) as AvatarExamOption[];
};

const isQuestionAnswered = (
  question: AvatarExamQuestion,
  answer: unknown
): boolean => {
  switch (question.tipo) {
    case 'test':
    case 'respuesta_corta':
    case 'quiz_bowl':
    case 'desarrollo':
      return typeof answer === 'string' && answer.trim().length > 0;
    case 'multiple_respuesta':
      return Array.isArray(answer) && answer.length > 0;
    case 'verdadero_falso':
      return typeof answer === 'boolean';
    case 'ordenacion':
    case 'oracion_confusa':
      return (
        Array.isArray(answer) &&
        answer.length === getQuestionOptions(question).length
      );
    case 'correspondencia': {
      const sources = question.datos?.origen ?? [];
      if (!sources.length || typeof answer !== 'object' || !answer) {
        return false;
      }

      return sources.every(
        (source) =>
          typeof (answer as Record<string, string>)[source.id] === 'string' &&
          (answer as Record<string, string>)[source.id].trim().length > 0
      );
    }
    case 'rellenar_hueco':
    case 'rellenar_huecos_multiple': {
      const gapIds = getHuecoIds(question);
      if (!gapIds.length || typeof answer !== 'object' || !answer) {
        return false;
      }

      return gapIds.every(
        (gapId) =>
          typeof (answer as Record<string, string>)[gapId] === 'string' &&
          (answer as Record<string, string>)[gapId].trim().length > 0
      );
    }
    case 'numerica':
      return answer !== '' && answer !== null && answer !== undefined;
    case 'opinion_likert':
      return answer !== '' && answer !== null && answer !== undefined;
    default:
      return false;
  }
};

const normalizeExamAnswer = (
  question: AvatarExamQuestion,
  answer: unknown
): unknown => {
  switch (question.tipo) {
    case 'test':
    case 'respuesta_corta':
    case 'quiz_bowl':
    case 'desarrollo':
      return typeof answer === 'string' ? answer.trim() : '';
    case 'multiple_respuesta':
    case 'ordenacion':
    case 'oracion_confusa':
      return Array.isArray(answer) ? answer : [];
    case 'verdadero_falso':
      return typeof answer === 'boolean' ? answer : null;
    case 'correspondencia':
    case 'rellenar_hueco':
    case 'rellenar_huecos_multiple':
      return Object.fromEntries(
        Object.entries((answer as Record<string, string>) ?? {}).map(
          ([key, value]) => [key, typeof value === 'string' ? value.trim() : '']
        )
      );
    case 'numerica':
      return answer === '' || answer === null || answer === undefined
        ? null
        : Number(answer);
    case 'opinion_likert':
      return answer === '' || answer === null || answer === undefined
        ? null
        : Number(answer);
    default:
      return answer;
  }
};

const InteractiveExam = ({
  response,
  onSubmitExam,
}: {
  response: AvatarResponse;
  onSubmitExam: (payload: string) => void;
}) => {
  const examPayload = response.respuesta_json as AvatarExamPayload | undefined;
  const questions = useMemo(
    () => examPayload?.examen?.preguntas ?? [],
    [examPayload]
  );
  const [answers, setAnswers] = useState<ExamAnswers>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  useEffect(() => {
    setAnswers((prevAnswers) => {
      let hasChanges = false;
      const nextAnswers = { ...prevAnswers };

      questions.forEach((question) => {
        if (
          (question.tipo === 'ordenacion' ||
            question.tipo === 'oracion_confusa') &&
          !Array.isArray(prevAnswers[question.id])
        ) {
          nextAnswers[question.id] = getQuestionOptions(question).map(
            (item) => item.id
          );
          hasChanges = true;
        }
      });

      return hasChanges ? nextAnswers : prevAnswers;
    });
  }, [questions]);

  useEffect(() => {
    setCurrentQuestionIndex(0);
    setIsSubmitted(false);
    setValidationMessage('');
  }, [questions]);

  const answeredQuestions = questions.filter((question) =>
    isQuestionAnswered(question, answers[question.id])
  ).length;
  const currentQuestion = questions[currentQuestionIndex];

  const updateAnswer = (questionId: string, value: unknown) => {
    if (isSubmitted) {
      return;
    }

    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: value,
    }));
    setValidationMessage('');
  };

  const validateQuestion = (question: AvatarExamQuestion): boolean => {
    const isAnswered = isQuestionAnswered(question, answers[question.id]);
    if (isAnswered) {
      setValidationMessage('');
      return true;
    }

    const message = 'Responde la pregunta actual antes de continuar.';
    setValidationMessage(message);
    showToast(message, 'warn');
    return false;
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex === 0) {
      return;
    }

    setValidationMessage('');
    setCurrentQuestionIndex((prevIndex) => prevIndex - 1);
  };

  const goToNextQuestion = () => {
    if (!currentQuestion) {
      return;
    }

    if (!isSubmitted && !validateQuestion(currentQuestion)) {
      return;
    }

    setCurrentQuestionIndex((prevIndex) =>
      Math.min(prevIndex + 1, questions.length - 1)
    );
  };

  const moveOrderedItem = (
    question: AvatarExamQuestion,
    itemIndex: number,
    direction: -1 | 1
  ) => {
    const currentItems = getOrderedItems(question, answers[question.id]);
    const targetIndex = itemIndex + direction;

    if (targetIndex < 0 || targetIndex >= currentItems.length) {
      return;
    }

    const reorderedItems = [...currentItems];
    const [movedItem] = reorderedItems.splice(itemIndex, 1);
    reorderedItems.splice(targetIndex, 0, movedItem);
    updateAnswer(
      question.id,
      reorderedItems.map((item) => item.id)
    );
  };

  const buildSubmissionPayload = () =>
    JSON.stringify({
      examen_resuelto: {
        titulo: examPayload?.examen?.titulo ?? null,
        preguntas: questions.map((question) => ({
          id: question.id,
          tipo: question.tipo,
          respuesta_usuario: normalizeExamAnswer(
            question,
            answers[question.id]
          ),
        })),
      },
    });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!questions.length) {
      return;
    }

    const unansweredQuestions = questions.filter(
      (question) => !isQuestionAnswered(question, answers[question.id])
    );

    if (unansweredQuestions.length > 0) {
      const missingCount = unansweredQuestions.length;
      const questionLabel = missingCount === 1 ? 'pregunta' : 'preguntas';
      const message = `Faltan ${missingCount} ${questionLabel} por responder antes de enviar el examen.`;
      setValidationMessage(message);
      showToast(message, 'warn');
      return;
    }

    setIsSubmitted(true);
    setValidationMessage('');
    onSubmitExam(buildSubmissionPayload());
  };

  const renderTextWithGaps = (question: AvatarExamQuestion) => {
    const textWithGaps = question.datos?.texto_con_huecos ?? '';
    const gapAnswers = (answers[question.id] as Record<string, string>) ?? {};
    const parts = textWithGaps.split(/(\[[^\]]+\])/g);

    return (
      <div className="interactive-exam-gap-text">
        {parts.map((part, index) => {
          const isGap = /^\[[^\]]+\]$/.test(part);

          if (!isGap) {
            return (
              <span key={`${question.id}-text-${index}`}>{part}</span>
            );
          }

          const gapId = part.slice(1, -1);

          return (
            <input
              key={`${question.id}-${gapId}`}
              type="text"
              className="interactive-exam-gap-input"
              value={gapAnswers[gapId] ?? ''}
              disabled={isSubmitted}
              onChange={(event) =>
                updateAnswer(question.id, {
                  ...gapAnswers,
                  [gapId]: event.target.value,
                })
              }
              placeholder={gapId.replace(/_/g, ' ')}
            />
          );
        })}
      </div>
    );
  };

  const renderQuestionInput = (question: AvatarExamQuestion) => {
    switch (question.tipo) {
      case 'test':
        return (
          <div className="interactive-exam-options">
            {getQuestionOptions(question).map((option) => {
              const isSelected = answers[question.id] === option.id;

              return (
                <label
                  key={option.id}
                  className={`interactive-exam-option ${isSelected ? 'interactive-exam-option-selected' : ''}`}
                >
                  <input
                    type="radio"
                    name={question.id}
                    value={option.id}
                    checked={isSelected}
                    disabled={isSubmitted}
                    onChange={() => updateAnswer(question.id, option.id)}
                  />
                  <span className="interactive-exam-option-text">
                    {option.texto}
                  </span>
                  {isSelected && (
                    <span className="interactive-exam-option-badge">
                      Seleccionada
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        );
      case 'multiple_respuesta': {
        const selectedValues = Array.isArray(answers[question.id])
          ? (answers[question.id] as string[])
          : [];

        return (
          <div className="interactive-exam-options">
            {getQuestionOptions(question).map((option) => {
              const isSelected = selectedValues.includes(option.id);

              return (
                <label
                  key={option.id}
                  className={`interactive-exam-option ${isSelected ? 'interactive-exam-option-selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    disabled={isSubmitted}
                    onChange={(event) => {
                      const nextValues = event.target.checked
                        ? [...selectedValues, option.id]
                        : selectedValues.filter((value) => value !== option.id);
                      updateAnswer(question.id, nextValues);
                    }}
                  />
                  <span className="interactive-exam-option-text">
                    {option.texto}
                  </span>
                  {isSelected && (
                    <span className="interactive-exam-option-badge">
                      Seleccionada
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        );
      }
      case 'verdadero_falso':
        return (
          <div className="interactive-exam-options">
            {[true, false].map((option) => {
              const isSelected = answers[question.id] === option;

              return (
                <label
                  key={`${question.id}-${String(option)}`}
                  className={`interactive-exam-option ${isSelected ? 'interactive-exam-option-selected' : ''}`}
                >
                  <input
                    type="radio"
                    name={question.id}
                    checked={isSelected}
                    disabled={isSubmitted}
                    onChange={() => updateAnswer(question.id, option)}
                  />
                  <span className="interactive-exam-option-text">
                    {option ? 'Verdadero' : 'Falso'}
                  </span>
                  {isSelected && (
                    <span className="interactive-exam-option-badge">
                      Seleccionada
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        );
      case 'desarrollo':
        return (
          <textarea
            className="interactive-exam-textarea"
            value={(answers[question.id] as string) ?? ''}
            disabled={isSubmitted}
            onChange={(event) => updateAnswer(question.id, event.target.value)}
            rows={5}
            placeholder="Escribe tu respuesta"
          />
        );
      case 'ordenacion':
      case 'oracion_confusa':
        return (
          <div className="interactive-exam-order-list">
            {getOrderedItems(question, answers[question.id]).map(
              (item, index, orderedItems) => (
                <div key={item.id} className="interactive-exam-order-item">
                  <span className="interactive-exam-order-label">
                    {index + 1}. {item.texto}
                  </span>
                  <div className="interactive-exam-order-actions">
                    <button
                      type="button"
                      className="interactive-exam-order-btn"
                      disabled={isSubmitted || index === 0}
                      onClick={() => moveOrderedItem(question, index, -1)}
                    >
                      Subir
                    </button>
                    <button
                      type="button"
                      className="interactive-exam-order-btn"
                      disabled={isSubmitted || index === orderedItems.length - 1}
                      onClick={() => moveOrderedItem(question, index, 1)}
                    >
                      Bajar
                    </button>
                  </div>
                </div>
              )
            )}
          </div>
        );
      case 'correspondencia': {
        const sourceItems = question.datos?.origen ?? [];
        const destinationItems = question.datos?.destino ?? [];
        const selectedMatches =
          (answers[question.id] as Record<string, string>) ?? {};

        return (
          <div className="interactive-exam-match-list">
            {sourceItems.map((source) => (
              <div key={source.id} className="interactive-exam-match-row">
                <span className="interactive-exam-match-source">
                  {source.texto}
                </span>
                <select
                  className="interactive-exam-select"
                  value={selectedMatches[source.id] ?? ''}
                  disabled={isSubmitted}
                  onChange={(event) =>
                    updateAnswer(question.id, {
                      ...selectedMatches,
                      [source.id]: event.target.value,
                    })
                  }
                >
                  <option value="">Selecciona una opción</option>
                  {destinationItems.map((destination) => (
                    <option key={destination.id} value={destination.id}>
                      {destination.texto}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        );
      }
      case 'rellenar_hueco':
      case 'rellenar_huecos_multiple':
        return renderTextWithGaps(question);
      case 'numerica':
        return (
          <input
            type="number"
            step="any"
            className="interactive-exam-input"
            value={(answers[question.id] as string | number) ?? ''}
            disabled={isSubmitted}
            onChange={(event) => updateAnswer(question.id, event.target.value)}
            placeholder="Escribe un valor numérico"
          />
        );
      case 'respuesta_corta':
        return (
          <input
            type="text"
            className="interactive-exam-input"
            value={(answers[question.id] as string) ?? ''}
            disabled={isSubmitted}
            onChange={(event) => updateAnswer(question.id, event.target.value)}
            placeholder="Escribe tu respuesta"
          />
        );
      case 'opinion_likert': {
        const scale = question.datos?.escala ?? [];
        const labels = question.datos?.etiquetas ?? {};

        return (
          <div className="interactive-exam-likert">
            <div className="interactive-exam-likert-options">
              {scale.map((value) => {
                const isSelected = answers[question.id] === value;

                return (
                  <label
                    key={`${question.id}-${value}`}
                    className={`interactive-exam-likert-option ${isSelected ? 'interactive-exam-likert-option-selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name={question.id}
                      checked={isSelected}
                      disabled={isSubmitted}
                      onChange={() => updateAnswer(question.id, value)}
                    />
                    <span>{value}</span>
                  </label>
                );
              })}
            </div>
            {(labels[String(scale[0])] || labels[String(scale[scale.length - 1])]) && (
              <div className="interactive-exam-likert-labels">
                <span>{labels[String(scale[0])] ?? ''}</span>
                <span>{labels[String(scale[scale.length - 1])] ?? ''}</span>
              </div>
            )}
          </div>
        );
      }
      case 'quiz_bowl':
        return (
          <>
            {(question.datos?.pistas?.length ?? 0) > 0 && (
              <ul className="interactive-exam-clues">
                {question.datos?.pistas?.map((hint, index) => (
                  <li key={`${question.id}-hint-${index}`}>{hint}</li>
                ))}
              </ul>
            )}
            <input
              type="text"
              className="interactive-exam-input"
              value={(answers[question.id] as string) ?? ''}
              disabled={isSubmitted}
              onChange={(event) => updateAnswer(question.id, event.target.value)}
              placeholder="Escribe tu respuesta"
            />
          </>
        );
      default:
        return (
          <textarea
            className="interactive-exam-textarea"
            value={String(answers[question.id] ?? '')}
            disabled={isSubmitted}
            onChange={(event) => updateAnswer(question.id, event.target.value)}
            rows={4}
            placeholder="Escribe tu respuesta"
          />
        );
    }
  };

  if (!questions.length) {
    return <>{response.respuesta_usuario}</>;
  }

  if (!currentQuestion) {
    return null;
  }

  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  return (
    <form className="interactive-exam" onSubmit={onSubmit}>
      <div className="interactive-exam-header">
        <p className="interactive-exam-intro">{response.respuesta_usuario}</p>
        <div className="interactive-exam-progress">
          Pregunta {currentQuestionIndex + 1} de {questions.length} · {answeredQuestions} / {questions.length} respondidas
        </div>
      </div>

      <section key={currentQuestion.id} className="interactive-exam-question">
        <div className="interactive-exam-question-header">
          <span className="interactive-exam-question-number">
            Pregunta {currentQuestionIndex + 1}
          </span>
          
        </div>
        <h3 className="interactive-exam-question-title">
          {currentQuestion.enunciado}
        </h3>
        {renderQuestionInput(currentQuestion)}
      </section>

      {validationMessage && (
        <div className="interactive-exam-validation">{validationMessage}</div>
      )}

      <div className="interactive-exam-footer">
        <div className="interactive-exam-navigation">
          <button
            type="button"
            className="interactive-exam-nav-btn"
            onClick={goToPreviousQuestion}
            disabled={currentQuestionIndex === 0}
          >
            Anterior
          </button>
          {!isLastQuestion && (
            <button
              type="button"
              className="interactive-exam-nav-btn interactive-exam-nav-btn-primary"
              onClick={goToNextQuestion}
            >
              Siguiente
            </button>
          )}
          {isLastQuestion && (
            <button
              type="submit"
              className="interactive-exam-submit-btn"
              disabled={isSubmitted}
              onClick={(event) => {
                if (!validateQuestion(currentQuestion)) {
                  event.preventDefault();
                }
              }}
            >
              {isSubmitted ? 'Respuestas enviadas' : 'Enviar respuestas'}
            </button>
          )}
        </div>
        {isSubmitted && (
          <span className="interactive-exam-submit-info">
            Se va a corregir el examen.
          </span>
        )}
      </div>
    </form>
  );
};

// Pulgar arriba = 5 y pulgar abajo = 1 en la escala 1-5 que agrega el backend
const PUNTUACION_BUENA = 5;
const PUNTUACION_MALA = 1;

const ThumbIcon = ({ down = false }: { down?: boolean }) =>
  down ? (
    <ThumbDownIcon className="satisfaction-inline__icon" aria-hidden="true" />
  ) : (
    <ThumbUpIcon className="satisfaction-inline__icon" aria-hidden="true" />
  );

// Valoración discreta con pulgares al pie de la respuesta de un asistente
const SatisfactionInline = ({
  response,
  hoverOnly = false,
}: {
  response: AvatarResponse;
  hoverOnly?: boolean;
}) => {
  const { rateSatisfaction } = useAvatarContext();
  // Se marca al instante (antes de la respuesta del backend) y se revierte si el envío falla
  const [pending, setPending] = useState<number | null>(null);
  const selected = response.valoracion_enviada ?? pending;

  const handleRate = async (puntuacion: number) => {
    if (selected) {
      return;
    }
    setPending(puntuacion);
    const ok = await rateSatisfaction(response.id, puntuacion);
    if (!ok) {
      setPending(null);
      showToast('No se pudo enviar la valoración.', 'warn');
    }
  };

  const renderButton = (puntuacion: number, label: string, down: boolean) => (
    <button
      type="button"
      className={`satisfaction-inline__thumb${selected === puntuacion ? ' is-selected' : ''}`}
      onClick={() => handleRate(puntuacion)}
      disabled={Boolean(selected)}
      title={selected ? 'Gracias por tu valoración' : label}
      aria-label={label}
      aria-pressed={selected === puntuacion}
    >
      <ThumbIcon down={down} />
    </button>
  );

  return (
    <div
      className={`satisfaction-inline${hoverOnly ? ' satisfaction-inline--hover-only' : ''}`}
      role="group"
      aria-label="Valora esta respuesta"
    >
      {renderButton(PUNTUACION_BUENA, 'Buena respuesta', false)}
      {renderButton(PUNTUACION_MALA, 'Mala respuesta', true)}
    </div>
  );
};

// Subcomponente Message
const Message = ({
  message,
  onSelect,
  onSubmitExam,
  loading,
  canRenderUploadInput,
  canRenderSurvey,
  isSurveyHoverOnly,
}) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [showPdfButton, setShowPdfButton] = useState(false);
  const [expandedImage, setExpandedImage] = useState<{ src: string; alt: string } | null>(null);
  const htmlContainerRef = useRef<HTMLDivElement | null>(null);
  const response = message?.response;
  const formatoRespuesta = response?.formato_respuesta_usuario;
  const htmlRespuesta = response?.respuesta_usuario;

  const handleOptionSelect = (optionText: string) => {
    if (!selectedOption) {
      setSelectedOption(optionText);
      onSelect(optionText);
    }
  };

  // Detección robusta: buscamos clases reales en el DOM renderizado (no en el string).
  useEffect(() => {
    if (formatoRespuesta?.toLowerCase() !== 'html') {
      setShowPdfButton(false);
      return;
    }

    const raf = requestAnimationFrame(() => {
      const container = htmlContainerRef.current;
      if (!container) {
        setShowPdfButton(false);
        return;
      }

      const hasSummary =
        !!container.querySelector('.resumen_visual') ||
        !!container.querySelector('.resumen-extenso');

      setShowPdfButton(hasSummary);
    });

    return () => cancelAnimationFrame(raf);
  }, [formatoRespuesta, htmlRespuesta]);

  useEffect(() => {
    if (!expandedImage) return;

    const previousBodyOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setExpandedImage(null);
      }
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [expandedImage]);

  const openImagePreview = (src?: string, alt = 'Imagen del mensaje') => {
    if (!src) return;
    setExpandedImage({ src, alt });
  };

  const handleDownloadPdf = async () => {
    const container = htmlContainerRef.current;
    if (!container) return;

    // Intentamos encontrar el bloque del resumen (si existe), para no incluir todo el mensaje.
    const summaryEl =
      (container.querySelector('.resumen_visual') as HTMLElement | null) ||
      (container.querySelector('.resumen-extenso') as HTMLElement | null) ||
      container;

    try {
      setIsDownloadingPdf(true);
      await downloadElementAsPdf(summaryEl, { filename: 'resumen.pdf' });
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const renderAttachedFiles = () => {
    if (!response?.files?.length) return null;

    return (
      <div className="message-files">
        {response.files.map((file, index) => (
          <FileIcon
            key={`${file.name}-${index}`}
            fileName={file.name}
            fileType={file.type}
          />
        ))}
      </div>
    );
  };

  const renderMessageByType = (response: AvatarResponse) => {
    switch (response?.formato_respuesta_usuario?.toLowerCase()) {
      case 'html':
        return (
          <div className="message-html">
            {showPdfButton && (
              <div className="summary-pdf-toolbar row-h-between">
                <div />
                <button
                  type="button"
                  className="summary-pdf-btn"
                  onClick={handleDownloadPdf}
                  disabled={isDownloadingPdf}
                  title="Descargar este resumen en PDF"
                >
                  {isDownloadingPdf ? 'Generando PDF…' : 'Descargar Resumen'}
                </button>
              </div>
            )}

            <div ref={htmlContainerRef} className="message-html-content">
              {parse(response.respuesta_usuario, {
                replace: (domNode) => {
                  if (
                    domNode.type === 'tag' &&
                    'attribs' in domNode &&
                    (domNode as any).attribs.class === 'option-cell'
                  ) {
                    const getText = (node): string => {
                      if (node.type === 'text') {
                        return node.data;
                      } else if (node.type === 'tag') {
                        return node.children.map(getText).join('');
                      }
                      return '';
                    };

                    const optionText = getText(domNode);
                    const isSelected = selectedOption === optionText;
                    const isDisabled = selectedOption && !isSelected;

                    return (
                      <div
                        className={`option-cell ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                        onClick={() => handleOptionSelect(optionText)}
                      >
                        {optionText}
                      </div>
                    );
                  }

                  if (
                    domNode.type === 'tag' &&
                    domNode.name === 'img' &&
                    'attribs' in domNode
                  ) {
                    const attribs = (domNode as any).attribs ?? {};
                    const src = attribs.src;
                    const alt = attribs.alt || attribs.title || 'Imagen del mensaje';
                    const className = ['message-img', 'message-img-clickable', attribs.class]
                      .filter(Boolean)
                      .join(' ');

                    return (
                      <img
                        src={src}
                        alt={alt}
                        title={attribs.title}
                        className={className}
                        loading="lazy"
                        role="button"
                        tabIndex={0}
                        onClick={(event) => {
                          event.stopPropagation();
                          openImagePreview(src, alt);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openImagePreview(src, alt);
                          }
                        }}
                      />
                    );
                  }
                },
              })}
            </div>
          </div>
        );
      case 'filetoshow':
        return (
          <>
            {loading ? (
              <div className="row-h-center">
                <LoadingSpinner />
              </div>
            ) : (
              <>
                {response.file && (
                  <>
                    <FileIcon
                      fileName={response.file.name}
                      fileType={response.file.type}
                    />
                    <br />
                  </>
                )}
                {response.respuesta_usuario}
                {response.file && (
                  <>
                    <br />
                    <div>
                      <FileDescriptionInput />
                      <div className="row-h-center">
                        <UploadFileCancelBtn message={response} />
                        <UploadFileSendBtn message={response} />
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        );
      case 'img':
        return (
          <>
            {response.respuesta_usuario}
            <br />
            <img
              src={response.url_img}
              alt="img"
              className="message-img message-img-clickable light-shadow"
              role="button"
              tabIndex={0}
              onClick={() => openImagePreview(response.url_img, 'Imagen del mensaje')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openImagePreview(response.url_img, 'Imagen del mensaje');
                }
              }}
            />
          </>
        );
      case 'exam':
        return (
          <InteractiveExam
            response={response}
            onSubmitExam={onSubmitExam}
          />
        );
      default:
        return response?.respuesta_usuario;
    }
  };

  const renderFileInput = (response: AvatarResponse) => {
    if (canRenderUploadInput && response?.archivo_upload === 'yes') {
      return (
        <>
          <br />
          <UploadFileInput idMessage={response.id} />
        </>
      );
    }
  };

  const renderMessageWidgets = (message) => {
    const dominio = message.response?.dominio;
    const rawTime = message.response?.time;
    const formattedTime = String(rawTime);

    return (
      <div className="message-time">
        {canRenderSurvey && (
          <SatisfactionInline
            response={message.response}
            hoverOnly={isSurveyHoverOnly}
          />
        )}
        {dominio !== 'GPT-CEU' && <span className="message-type">{dominio}</span>}
        {formattedTime}
      </div>
    );
  };

  return (
    <div className={`message ${message.sender} light-shadow`}>
     {/* {message.author && <b>{message.author}: </b>} */}
      {renderAttachedFiles()}
      {renderMessageByType(message.response)}
      {renderFileInput(message.response)}
      {message.sender === 'bot' && renderMessageWidgets(message)}
      {message.sender === 'user' && (
        <div className="message-time">{String(message.response?.time)}</div>
      )}
      {expandedImage && (
        <div
          className="image-preview-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={() => setExpandedImage(null)}
        >
          <div className="image-preview-dialog" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="image-preview-close"
              aria-label="Cerrar imagen ampliada"
              onClick={() => setExpandedImage(null)}
            >
              x
            </button>
            <img
              src={expandedImage.src}
              alt={expandedImage.alt}
              className="image-preview-img"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Componente Chat
const Chat = ({ messages, isBotThinking }: ChatProps) => {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const { loading } = useChatContext();
  const { talk } = useAvatarContext();
  const lastAssistantMessageIndex = messages.reduce((lastIndex, currentMessage, index) => {
    return currentMessage.sender === 'bot' ? index : lastIndex;
  }, -1);
  const activeUploadMessageIndex =
    lastAssistantMessageIndex >= 0 &&
    messages[lastAssistantMessageIndex]?.response?.archivo_upload === 'yes'
      ? lastAssistantMessageIndex
      : -1;

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
    localStorage.setItem('theme', 'dark');
  }, []);

  useEffect(() => {
    messagesContainerRef.current.scrollTo({
      behavior: 'smooth',
      top: messagesContainerRef.current.scrollHeight,
    });
    createEventListeners(talk);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const handleSelect = (selection: string) => {
    talk(selection, true); // Enviar la selección al backend sin mostrarla en el chat
  };

  const handleExamSubmit = (payload: string) => {
    talk(payload, true); // Enviar las respuestas del examen sin mostrar el mensaje del usuario en el chat
  };

  return (
    <div ref={messagesContainerRef} className="messages column">
      {messages.map((message, index) => (
        <Message
          key={index}
          message={message}
          onSelect={handleSelect}
          onSubmitExam={handleExamSubmit}
          loading={loading}
          canRenderUploadInput={index === activeUploadMessageIndex}
          canRenderSurvey={
            message.sender === 'bot' &&
            !isConversationEndedResponse(message.response)
          }
          isSurveyHoverOnly={index !== lastAssistantMessageIndex}
        />
      ))}
      <LoadingMessage isBotThinking={isBotThinking} />
    </div>
  );
};

export default Chat;